import { addNotification } from "@/redux/reducer/notificationSlice";
import { Dispatch } from "redux";
import { signOut } from "next-auth/react";
import Router from "next/router";

type ApiOptions = RequestInit & {
  onUnauthorized?: () => void;
};

interface QueuedRequest {
  url: string;
  options: ApiOptions;
  dispatch?: Dispatch<any>;
}

let requestQueue: QueuedRequest[] = [];
let isProcessingQueue = false;

export const retryQueuedRequests = async () => {
  if (!navigator.onLine || isProcessingQueue || requestQueue.length === 0)
    return;

  isProcessingQueue = true;

  const requestsToProcess = [...requestQueue];
  requestQueue = [];

  try {
    await Promise.all(
      requestsToProcess.map(async ({ url, options, dispatch }) => {
        try {
          const response = await fetch(url, options);
          if (response.ok) {
            return;
          }
        } catch (error) {
          console.error("Error processing queued request:", error);
        }
      })
    );
  } finally {
    isProcessingQueue = false;
  }
};

const apiFetch = async (
  url: string,
  options: ApiOptions = {},
  dispatch?: Dispatch<any>,
) => {
  if (!navigator.onLine) {
    if (!requestQueue.some((req) => req.url === url)) {
      if (dispatch) {
        requestQueue.push({ url, options, dispatch });
        dispatch(
          addNotification({
            type: "error",
            title: "Connection Error",
            subtitle: "Connection lost. Please check your internet connection.",
          })
        );
      } else {
        requestQueue.push({ url, options });
      }
    }
    return { error: "Connection lost. Please check your internet connection.", code: 598 };
  }

  try {
    const res = await fetch(url, options);
    if (res.status === 401) {
      options.onUnauthorized?.();
      await signOut({ redirect: false });
      Router.replace("/signin");
      return { status: 401 };
    }

    return await res.json();
  } catch (error: any) {
    if (error.message === "Failed to fetch") {
      if (!requestQueue.some((req) => req.url === url)) {
        requestQueue.push({ url, options, dispatch });
      }
    }
    return { error: error.message };
  }
};

export default apiFetch;
